import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  // Ensure page loads at the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const SectionTitle = ({ children }) => (
    <h2 className="text-xl font-black text-[#0F2F63] mt-8 mb-4 border-b border-gray-100 pb-2">
      {children}
    </h2>
  );

  const BulletPoint = ({ children }) => (
    <li className="flex items-start mb-2.5">
      <span className="text-[#FF7A59] mr-3 mt-1.5"><i className="fas fa-circle text-[6px]"></i></span>
      <span className="text-gray-600 font-medium leading-relaxed flex-1">{children}</span>
    </li>
  );

  return (
    <div className="py-6 px-4 md:px-8 max-w-4xl mx-auto w-full min-h-[70vh] animate-fade-in">
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6 border-b border-gray-100 pb-5">
        <button 
          onClick={() => navigate(-1)} 
          className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600 transition-colors"
        >
          <i className="fas fa-arrow-left"></i>
        </button>
        <h1 className="text-2xl md:text-3xl font-black text-[#0F2F63] flex items-center gap-3">
          <i className="fas fa-user-shield text-[#FF7A59]"></i> Privacy Policy
        </h1>
      </div>

      {/* Content Container */}
      <div className="bg-white p-6 md:p-10 rounded-3xl shadow-sm border border-gray-100">
        
        <SectionTitle>1. Introduction</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          Welcome to SV MART ("we," "our," or "us"). This Privacy Policy describes our policies and procedures on the collection, use, and disclosure of your information when you use our mobile application and website (collectively, the "Service"). It also explains your privacy rights and how applicable laws protect you. By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.
        </p>

        <SectionTitle>2. Interpretation and Definitions</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          <strong className="text-gray-800">Interpretation:</strong> Words with capitalized initial letters have meanings defined under the following conditions. These definitions apply regardless of whether they appear in singular or plural form.
        </p>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          <strong className="text-gray-800">Definitions:</strong> For the purposes of this Privacy Policy:
        </p>
        <ul className="mb-4">
          <BulletPoint><strong className="text-gray-800">Application</strong> means the SV MART mobile or web application.</BulletPoint>
          <BulletPoint><strong className="text-gray-800">Company</strong> ("we", "us", or "our") refers to SV MART.</BulletPoint>
          <BulletPoint><strong className="text-gray-800">Country</strong> refers to India.</BulletPoint>
          <BulletPoint><strong className="text-gray-800">Device</strong> means any device that can access the Service, such as a mobile phone, tablet, or computer.</BulletPoint>
          <BulletPoint><strong className="text-gray-800">Personal Data</strong> means any information that relates to an identified or identifiable individual.</BulletPoint>
          <BulletPoint><strong className="text-gray-800">Service</strong> refers to the SV MART platform (Website and Mobile Application).</BulletPoint>
          <BulletPoint><strong className="text-gray-800">Service Provider</strong> means any third-party company or individual who processes data on behalf of the Company.</BulletPoint>
          <BulletPoint><strong className="text-gray-800">Usage Data</strong> refers to data collected automatically from the Service.</BulletPoint>
          <BulletPoint><strong className="text-gray-800">You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</BulletPoint>
        </ul>

        <SectionTitle>3. Information We Collect</SectionTitle>
        <h3 className="text-lg font-bold text-gray-800 mb-2 mt-4">A. Personal Data</h3>
        <p className="text-gray-600 font-medium leading-relaxed mb-3">
          We collect personal information that you voluntarily provide to us when registering, placing an order, or interacting with our platform. This includes:
        </p>
        <ul className="mb-6">
          <BulletPoint><strong className="text-gray-700">Personal Details:</strong> Name, phone number (for OTP authentication), and email address.</BulletPoint>
          <BulletPoint><strong className="text-gray-700">Precise Location Data:</strong> GPS-based location data to provide a localized shopping experience.</BulletPoint>
          <BulletPoint><strong className="text-gray-700">Device & Usage Data:</strong> Push notification tokens and local storage data to improve user experience.</BulletPoint>
        </ul>

        <h3 className="text-lg font-bold text-gray-800 mb-2 mt-4">B. Automatic Location Access</h3>
        <p className="text-gray-600 font-medium leading-relaxed mb-3">
          With your prior permission, we collect precise location data (GPS) to enable core features of the application. We use location data to provide a location-based shopping experience and to ensure accurate service delivery. Specifically:
        </p>
        <ul className="mb-3">
          <BulletPoint><strong className="text-gray-700">Dynamic Product Availability:</strong> To display products available in your region based on proximity to local farms or warehouses.</BulletPoint>
          <BulletPoint><strong className="text-gray-700">Delivery Optimisation:</strong> To determine the most efficient delivery route and ensure timely delivery of perishable goods.</BulletPoint>
          <BulletPoint><strong className="text-gray-700">Location-Based Notifications:</strong> To send relevant notifications based on your geographic area.</BulletPoint>
        </ul>
        <p className="text-gray-600 font-medium leading-relaxed mb-6">
          <strong className="text-gray-700">Improved User Experience:</strong> To automatically detect your location and reduce the need for manual address entry. If location access is disabled, certain features may be limited, and you may be required to manually enter your address.
        </p>

        <h3 className="text-lg font-bold text-gray-800 mb-2 mt-4">C. Usage Data</h3>
        <p className="text-gray-600 font-medium leading-relaxed mb-3">
          We automatically collect certain information when you use the Service, including:
        </p>
        <ul className="mb-6">
          <BulletPoint>Log Data: Information such as your device's Internet Protocol (IP) address, browser type, operating system, and the date and time of your visit.</BulletPoint>
          <BulletPoint>Device type and operating system.</BulletPoint>
          <BulletPoint>Pages or screens viewed.</BulletPoint>
          <BulletPoint>Time spent on pages.</BulletPoint>
        </ul>

        <h3 className="text-lg font-bold text-gray-800 mb-2 mt-4">D. Device Data</h3>
        <ul className="mb-6">
          <BulletPoint>Push notification tokens.</BulletPoint>
          <BulletPoint>Device identifiers.</BulletPoint>
          <BulletPoint>Local storage data.</BulletPoint>
        </ul>

        <SectionTitle>4. Payment Information</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          We use Razorpay as a third-party payment gateway to process payments securely. We do not collect, store, or process your debit or credit card information on our servers.
        </p>

        <SectionTitle>5. How We Use Your Information</SectionTitle>
        <ul className="mb-4">
          <BulletPoint>To authenticate users and provide secure access.</BulletPoint>
          <BulletPoint>Instantly customizing the app dashboard based on your GPS coordinates.</BulletPoint>
          <BulletPoint>Processing payments and ensuring 100% delivery accuracy.</BulletPoint>
          <BulletPoint>Sending location-triggered updates regarding order status and regional offers.</BulletPoint>
          <BulletPoint>To process orders and facilitate deliveries.</BulletPoint>
          <BulletPoint>To improve application performance and functionality.</BulletPoint>
          <BulletPoint>To detect and prevent fraud or misuse.</BulletPoint>
        </ul>

        <SectionTitle>6. Sharing of Your Information</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-3">
          We do not sell your personal data. We may share your information in the following cases:
        </p>
        <ul className="mb-3">
          <BulletPoint>With Service Providers to support app functionality (delivery, analytics, notifications).</BulletPoint>
          <BulletPoint>With payment processors (Razorpay) for transaction processing.</BulletPoint>
          <BulletPoint>When required by law or to comply with legal obligations.</BulletPoint>
        </ul>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          We ensure that any third parties we share your data with are compliant with applicable data protection laws and have appropriate security measures in place. We do not share your precise location data with third parties for marketing purposes without your explicit consent.
        </p>

        <SectionTitle>7. Data Retention</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-3">
          We retain your personal data only for as long as necessary to fulfil the purposes outlined in this Privacy Policy, including:
        </p>
        <ul className="mb-3">
          <BulletPoint>Maintaining your account.</BulletPoint>
          <BulletPoint>Improving services.</BulletPoint>
          <BulletPoint>Complying with legal obligations.</BulletPoint>
        </ul>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          We may delete or anonymize data once it is no longer required.
        </p>

        <SectionTitle>8. Data Security</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          We implement appropriate technical and organisational measures to protect your personal data. However, no method of transmission over the Internet or electronic storage is completely secure, and we cannot guarantee absolute security.
        </p>

        <SectionTitle>9. Your Data Protection Rights</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-3">You have the right to:</p>
        <ul className="mb-3">
          <BulletPoint>Access your personal data.</BulletPoint>
          <BulletPoint>Update or correct your information.</BulletPoint>
          <BulletPoint>Request deletion of your data.</BulletPoint>
          <BulletPoint>Disable location access through your device settings.</BulletPoint>
        </ul>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          To exercise these rights, please contact us. Disabling location access may affect certain features of the Service.
        </p>

        <SectionTitle>10. Children’s Privacy</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          Our Service is not intended for individuals under the age of 16. We do not knowingly collect personal data from children. If we become aware that we have collected personal data from a child under 16, we will take steps to delete that information as soon as possible.
        </p>

        <SectionTitle>11. Third-Party Services</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          Our Service may contain links to third-party websites or services. We are not responsible for the privacy practices of such third parties.
        </p>

        <SectionTitle>12. Changes to This Privacy Policy</SectionTitle>
        <p className="text-gray-600 font-medium leading-relaxed mb-4">
          We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
        </p>

      </div>
    </div>
  );
};

export default PrivacyPolicyPage;